/**
 * Anomaly rules (`12-chain.md` § 5).
 *
 * The endpoint list shows that anomalies exist and nothing about what they
 * are, so this is a decision rather than a transcription: a small, explicit
 * rule set, each rule a rate compared against a threshold that a merchant can
 * move, and each flag carrying the report it came from.
 *
 * "**Do not ship an unexplained flag**; an operations alert that cannot be
 * drilled into gets ignored within a week." So every rule here names the
 * report that shows its working, and the engine that evaluates them fills in
 * the store and the day.
 *
 * A rule is code. Only its threshold is data — see `pos_insight_rule_settings`.
 */

export type InsightRule = {
  readonly code: InsightRuleCode;
  /** A message key. The word for a void differs by market as much as any other. */
  readonly labelKey: string;
  /** Where the flag is explained, drilled to with the store and period on it. */
  readonly reportKey: string;
  /** Basis points, out of the box. A merchant moves it; nobody edits code. */
  readonly defaultThresholdBp: number;
  /**
   * Which way is bad. A discount rate is flagged when it goes *above* the
   * threshold; spend per cover when it falls *below* the chain's own median by
   * more than the threshold.
   */
  readonly direction: "above" | "below";
};

export const INSIGHT_RULE_CODES = [
  "discount_rate",
  "void_rate",
  "refund_rate",
  "cover_spend",
] as const;

export type InsightRuleCode = (typeof INSIGHT_RULE_CODES)[number];

export const INSIGHT_RULES: readonly InsightRule[] = [
  {
    code: "discount_rate",
    labelKey: "insights.rule.discountRate",
    reportKey: "discounts",
    // 15% of gross given away is high for a restaurant and normal for none of
    // them; the number is a starting point, not a claim.
    defaultThresholdBp: 1500,
    direction: "above",
  },
  {
    code: "void_rate",
    labelKey: "insights.rule.voidRate",
    reportKey: "voids",
    defaultThresholdBp: 1000,
    direction: "above",
  },
  {
    code: "refund_rate",
    labelKey: "insights.rule.refundRate",
    reportKey: "master",
    defaultThresholdBp: 500,
    direction: "above",
  },
  {
    code: "cover_spend",
    // Measured against the chain's own median rather than a fixed sum, because
    // a food court and a fine-dining room are not comparable and a chain's
    // stores mostly are.
    labelKey: "insights.rule.coverSpend",
    reportKey: "master",
    defaultThresholdBp: 3000,
    direction: "below",
  },
];

export function findInsightRule(code: string): InsightRule | undefined {
  return INSIGHT_RULES.find((rule) => rule.code === code);
}

export function isInsightRuleCode(value: string): value is InsightRuleCode {
  return INSIGHT_RULE_CODES.includes(value as InsightRuleCode);
}
